import { buildCableServiceProduct } from './cableServices/cableServiceBuilder';
import { ProductData, CustomerInfo } from './helper';
import { buildVoiceServiceProduct } from './voiceServices/voiceProductBuilder';
import { OrderDetails, Services, T_Coupon, Product, BroadbandProduct, BillingCampaign } from '../../types';
import { IBroadbandInstallation } from '../../store/broadbandStore';
import { IBroadbandDelivery } from '../../store/broadbandDeliveryStore';
import { AddressPrequalification } from '../../services/prequal';
import { IUpgradableBoxesStore } from '../../store/upgradableBoxesStore';
import { VoiceDataForm } from '../voiceFormFields';
import {
  broadbandCategories,
  bundle_Offer_CouponCode,
  categoryIds,
  CREDIT_OFFER,
  voiceCategories,
  VTVMigrationType,
} from '../../config';
import { AddressService } from '../../services/address';
import { buildDataServiceProduct } from './dataServices/dataServiceBuilder';
import { TrackerService } from '../../services/tracker';
import { IArrowDelivery } from '../../store/arrowDeliveryStore';
import { boxesStoreApi } from '../../store/boxesStore';
import { productSkuIds } from '../../config';
import {
  couponStoreApi,
  getActiveCampaigns,
  NZPostAddressService,
  orderStoreApi,
  productStoreApi,
  useCouponStore,
} from '../../entry';

const getBroadbandDetails = async (trackingService: TrackerService, houseNumber: string) => {
  let tracking;
  try {
    tracking = await trackingService.getBroadbandDetails(houseNumber);
  } catch {
    tracking = undefined;
  }
  return tracking;
};

export const createRequestBody = async (
  trackerService: TrackerService,
  addressService: AddressService,
  nzPostAddressService: NZPostAddressService | undefined,
  details: IBroadbandInstallation | undefined,
  deliveryDetails: IBroadbandDelivery | undefined,
  orderProductsAddedToCart: ProductData[],
  orderProductsDowngraded: ProductData[],
  customer: CustomerInfo,
  billingServices:
    | Pick<
        Services,
        'CableServiceOccurrence' | 'DataServiceOccurrence' | 'TelephoneServiceOccurrence' | 'OTTServiceOccurrence'
      >
    | undefined,
  coupons: Pick<T_Coupon, 'description' | 'couponCode' | 'custom5'>[] | undefined,
  getPrequalData: (addressId: string) => Promise<AddressPrequalification | undefined>,
  upgradableBoxDetails:
    | Pick<IUpgradableBoxesStore, 'boxInstallRequired' | 'installationDate' | 'installationTime'>
    | undefined,
  voiceDetails: VoiceDataForm | undefined,
  currentlySubscribedBroadbandPackage: ProductData | undefined,
  hasActiveOrPendingMeshBroadbandPackage?: boolean,
  hasActiveStarter: boolean = true,
  arrowDeliveryDetails?: IArrowDelivery | undefined,
  fusionRegion?: string,
  vtvTransfer?: boolean,
  vtvTransferType?: VTVMigrationType,
  currentExistingCoupon?: BillingCampaign[] | undefined
): Promise<OrderDetails> => {
  const request: OrderDetails = {
    products: [] as Product[] | BroadbandProduct[],
  };
  //////////////////////
  //  DTH Section     //
  //////////////////////
  const orderPackages = orderProductsAddedToCart.filter(p => p.categoryId !== categoryIds.box);
  const cableProductDowngrades = orderProductsDowngraded.filter(
    p => !broadbandCategories.values.includes(p.categoryId) && !voiceCategories.values.includes(p.categoryId)
  );
  const boxes = boxesStoreApi.getState().boxes;

  // removing bundle coupons from upgradre journey order request
  const isUpgradeJourney = productStoreApi
    .getState()
    .getBasketItemsToAddToOrder()
    ?.some(p => p.currentlySubscribed === true);

  const voucher = couponStoreApi?.getState()?.voucher;
  const eligibilityOffers = couponStoreApi?.getState()?.eligibilityOffers;

  let cableCoupons;
  if (isUpgradeJourney) {
    const excludeCampaignCodes = eligibilityOffers?.eligibilityExcludeCampaign?.split(',').map(c => c.trim());
    const includedCampaignCodes = eligibilityOffers?.eligibilityIncludeCampaign?.split(',').map(c => c.trim());
    const existingActiveBundleCoupons = getActiveCampaigns(currentExistingCoupon ?? []) ?? [];
    // Now filter the existing bundle coupons that match any in the list
    const filteredActivBundleCoupons = existingActiveBundleCoupons.filter((c: any) =>
      bundle_Offer_CouponCode.includes(c.promotionCode)
    );

    const isAnyExcludeCampaignActive = filteredActivBundleCoupons?.some((c: any) =>
      excludeCampaignCodes?.includes(c.promotionCode)
    );
    const isAnyIncludedCampaignActive = filteredActivBundleCoupons?.some((c: any) =>
      includedCampaignCodes?.includes(c.promotionCode)
    );
    if (excludeCampaignCodes || includedCampaignCodes) {
      if (isAnyIncludedCampaignActive) {
        cableCoupons = coupons?.filter(cp => !bundle_Offer_CouponCode.includes(cp.couponCode));
      } else {
        cableCoupons = coupons;
      }
    } else {
      cableCoupons = coupons?.filter(cp => !bundle_Offer_CouponCode.includes(cp.couponCode));
    }
    // console.log("EE-Offer final cableCoupons",coupons,cableCoupons,isAnyIncludedCampaignActive)
  } else {
    //WAA-6037 upsell coupon for starter 12 months contract
    const hasUpSellCoupon = orderProductsAddedToCart?.filter((p: any) => p?.custom2); //custom2 upsell coupon field in konkart
    const isBundle = coupons?.some(cp => bundle_Offer_CouponCode.includes(cp?.couponCode));
    if (hasUpSellCoupon?.length > 0 && !isBundle && !voucher?.isVoucher) {
      const upSellCoupon = hasUpSellCoupon?.map((cp: any) => {
        return {
          couponCode: cp?.custom2,
          custom3: 'ACQUISITION',
          custom5: 'CABLE',
          description: 'upsell coupon contract',
        };
      });
      coupons = [...coupons!, ...upSellCoupon];
    }
  }

  // Check if that single bundle coupon already exists in existingOffers
  const isBundleCouponAlreadyExist = getActiveCampaigns(currentExistingCoupon ?? [])?.some((c: any) => {
    return bundle_Offer_CouponCode.includes(c.promotionCode);
  });

  // Create cable order details
  let cableServiceProduct = await buildCableServiceProduct(
    orderProductsAddedToCart,
    cableProductDowngrades,
    billingServices,
    upgradableBoxDetails,
    isUpgradeJourney ? cableCoupons : coupons,
    addressService,
    nzPostAddressService,
    customer,
    fusionRegion,
    boxes,
    arrowDeliveryDetails,
    vtvTransfer,
    vtvTransferType
  );
  if (voucher?.isVoucher) {
    request.voucherCode = voucher.voucherCode;
    // removing credit offer otc sku for multiroom boxes
    const creditOfferProduct = orderStoreApi
      .getState()
      ?.order?.orderProducts?.find(p => p.product.custom9 === CREDIT_OFFER);
    cableServiceProduct.services.forEach((item: any, index: number) => {
      let hasCreditIndex = item.actions?.[0]?.add.indexOf(creditOfferProduct?.product?.sku);
      if (hasCreditIndex !== -1 && index > 0) {
        item.actions?.[0]?.add.splice(hasCreditIndex, 1);
      }
    });
  }
  if (
    cableServiceProduct.services.length > 0 ||
    (cableServiceProduct?.campaigns && cableServiceProduct?.campaigns?.length > 0)
  ) {
    request.products.push(cableServiceProduct);
  }

  ///////////////////////////////
  // Broadband + Voice Section //
  ///////////////////////////////

  // Broadband active if theres an order package with a broadband categoryID
  const isSomeBroadbandProductInCart = !!orderPackages?.some(
    orderProduct =>
      orderProduct.categoryId === categoryIds.broadband ||
      orderProduct.categoryId === categoryIds.broadbandServices ||
      orderProduct.categoryId === categoryIds.broadbandOneOffFee ||
      orderProduct.categoryId === categoryIds.broadbandDevices
  );

  // Voice active if theres an order package with a voice categoryID
  const hasVoicePackage = orderPackages.some(p => voiceCategories.values.includes(p.categoryId));

  // if no Broadband and Voice products, just return the created request
  if (!isSomeBroadbandProductInCart && !hasVoicePackage) return request;

  let broadbandDetails;
  if (currentlySubscribedBroadbandPackage) {
    broadbandDetails = await getBroadbandDetails(trackerService, customer.houseNumber ?? '');
    // if something went wrong with the broadband-details information, prevent the order creation.
    if (!broadbandDetails?.dataProviderUserId) {
      throw new Error('Broadband details missing dataProviderUserId information');
    }
  }

  let dataServiceProduct = await buildDataServiceProduct(
    // CONTINUE HERE WITH BB AND TECH FEE
    trackerService,
    orderPackages,
    coupons,
    details,
    deliveryDetails,
    customer,
    getPrequalData,
    billingServices,
    addressService,
    currentlySubscribedBroadbandPackage,
    hasActiveOrPendingMeshBroadbandPackage,
    broadbandDetails
  );

  if (dataServiceProduct && dataServiceProduct.services.length > 0) {
    const isStarter = orderStoreApi
      .getState()
      .order?.orderProducts?.some(p => p.product.sku === productSkuIds.starter.primary);
    const isFibreStarter = orderStoreApi
      .getState()
      .order?.orderProducts?.some(
        p => p.product.sku === productSkuIds.broadbandEssnFibre.primary && !p.currentlySubscribed
      );
    const isFibreInDataOrder = dataServiceProduct?.services?.[0]?.actions?.[0]?.add?.some(
      p => p === productSkuIds.broadbandStarterCreditDiscount.primary
    );
    if (isFibreStarter && !isFibreInDataOrder && isStarter) {
      dataServiceProduct?.services?.[0]?.actions?.[0]?.add?.push(productSkuIds.broadbandStarterCreditDiscount.primary);
    }
    request.products.push(dataServiceProduct);
  }

  // if no Voice product, just return the created request
  if (!hasVoicePackage) return request;

  if (voiceDetails && hasVoicePackage) {
    // VOICE SECTION PAYLOAD BUILDER
    let voiceServiceProduct = await buildVoiceServiceProduct(
      orderPackages,
      coupons,
      voiceDetails,
      customer,
      getPrequalData,
      details,
      billingServices?.TelephoneServiceOccurrence,
      currentlySubscribedBroadbandPackage?.sku,
      billingServices,
      !!currentlySubscribedBroadbandPackage,
      broadbandDetails
    );

    if (voiceServiceProduct.services.length > 0) {
      request.products.push(voiceServiceProduct);
    }
  }

  return request;
};
